import { z } from 'zod'

/**
 * Un schema que VALIDA lo que declara y CONSERVA lo que no.
 *
 * POR QUÉ EXISTE. Las secciones del expediente FEIC —solicitante, PEP, económica,
 * patrimonial, representantes— están guardadas como `Mixed`: la base no tiene
 * contrato y quien las escribe es el formulario interno, que nombra varias cosas
 * distinto del SSOT (el vocabulario del PORTAL). `dependecyRelations` con su
 * typo, `businessRelationship` donde el portal dice `purpose`, `entityAddress`
 * donde dice `address`, y un `disabled` en CADA fila repetible.
 *
 * Zod, por defecto, DESCARTA lo que no declara. Un contrato de escritura armado
 * sobre el shape del SSOT valida bien y persiste mal: guarda un expediente al que
 * le faltan justo los campos que el otro vocabulario nombraba, y nadie se entera
 * porque no hay error. Eso ya pasó: un `PATCH` respondió 200 y el expediente
 * perdió sus relaciones de dependencia.
 *
 * PROFUNDO A PROPÓSITO. `passthrough()` de zod sólo abre el nivel de arriba, y la
 * pérdida más común está más abajo: el `disabled` de cada fila. Este helper abre
 * todos los objetos del árbol, incluidos los que viven dentro de arrays,
 * opcionales y defaults.
 *
 * NO ES LAXITUD, ES HONESTIDAD sobre de quién es el dato: lo declarado se sigue
 * validando —tipos, formatos, requeridos— y lo demás se conserva porque le
 * pertenece a una superficie que todavía no está unificada.
 *
 * CUÁNDO SE BORRA ESTE HELPER. No cuando el diccionario de campos sea uno solo: eso
 * unifica ETIQUETAS, no la forma del dato guardado. Se borra cuando NINGÚN escritor
 * produzca el vocabulario viejo, o sea cuando muera el formulario interno FEIC y los
 * expedientes existentes se migren (expand/contract). Son dos trabajos distintos y el
 * segundo es el caro; ver `docs/architecture/forms-unification-strategy.md`.
 */
export function openShape<T extends z.ZodTypeAny>(schema: T): T {
  const def = schema._def as { typeName: string } & Record<string, never>

  switch (def.typeName) {
    case z.ZodFirstPartyTypeKind.ZodObject: {
      const object = schema as unknown as z.ZodObject<z.ZodRawShape>
      const shape = Object.fromEntries(
        Object.entries(object.shape).map(([key, value]) => [key, openShape(value)])
      )
      return z.object(shape).passthrough() as unknown as T
    }
    // El elemento se abre, pero las reglas del ARRAY se conservan: perder un
    // `min(1)` acá sería aflojar una validación de verdad —«al menos una
    // nacionalidad»— por reconstruir el schema, y eso no es lo que se vino a
    // hacer.
    case z.ZodFirstPartyTypeKind.ZodArray: {
      const array = schema as unknown as z.ZodArray<z.ZodTypeAny>
      const { minLength, maxLength, exactLength } = array._def
      let opened = z.array(openShape(array.element))
      if (exactLength) opened = opened.length(exactLength.value, exactLength.message)
      if (minLength) opened = opened.min(minLength.value, minLength.message)
      if (maxLength) opened = opened.max(maxLength.value, maxLength.message)
      return opened as unknown as T
    }
    case z.ZodFirstPartyTypeKind.ZodOptional: {
      const optional = schema as unknown as z.ZodOptional<z.ZodTypeAny>
      return openShape(optional.unwrap()).optional() as unknown as T
    }
    case z.ZodFirstPartyTypeKind.ZodNullable: {
      const nullable = schema as unknown as z.ZodNullable<z.ZodTypeAny>
      return openShape(nullable.unwrap()).nullable() as unknown as T
    }
    case z.ZodFirstPartyTypeKind.ZodDefault: {
      const withDefault = schema as unknown as z.ZodDefault<z.ZodTypeAny>
      return openShape(withDefault.removeDefault()).default(
        withDefault._def.defaultValue()
      ) as unknown as T
    }
    // Las reglas cruzadas (`superRefine`) se conservan: envuelven al objeto ya
    // abierto, así que siguen corriendo sobre los campos declarados.
    case z.ZodFirstPartyTypeKind.ZodEffects: {
      const effects = schema as unknown as z.ZodEffects<z.ZodTypeAny>
      return new z.ZodEffects({
        ...effects._def,
        schema: openShape(effects.innerType())
      }) as unknown as T
    }
    default:
      return schema
  }
}
